import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react'

import { ErrorBox, Loading } from '../../components/DataTable'
import { StageTrack, type Stage } from '../../components/kit'
import { workflows as fetchWorkflows, type Workflow, type WorkflowAuthority } from '../../lib/workflows'
import { RemediationDeadlines } from './RemediationDeadlines'
import { VendorRequests } from './VendorRequests'
import '../../styles/workflows.css'

// Settings, Workflows: every process this programme runs, drawn from the rules
// it runs on (the product officer's table of 21 September 2026, item T10).
//
// # WHY THE DIAGRAMS ARE THE API'S
//
// Each stage is read by the API from the thing that enforces it: the onboarding
// steps in force, the exit chain the exit gate reads, the chasing order the
// reminder ladder follows. Drawing them here by hand would be a second copy of
// each rule that is right only until somebody changes the rule. This screen
// lays them out and sends a person to the screen that edits each one.
//
// # WHY THE DEADLINES OPEN HERE
//
// The remediation deadlines have no other home, so their editor opens over
// this page. The address carries it (?edit=finding-remediation), so the Edit
// button is a link like every other one on the page and can be sent to
// somebody.

const EDIT_REMEDIATION = 'finding-remediation'
// Who approves a vendor request (release 3.3), which has no other home either.
const EDIT_REQUESTS = 'vendor-requests'

// # WHY THE DIAGRAM CHOOSES ITS OWN FORM
//
// A stage of the track is a column of its own, and a column narrower than the
// longest word in it breaks that word in the middle ("Questionnair/e"): the
// kit's stage label is allowed to break anywhere so that a long code cannot
// push the track wider than its card. Which window width is too narrow depends
// on how much of the window the card gets, and that moved when Settings gained
// a list beside its pane, so a media query on the window was always going to be
// wrong for somebody. The diagram measures itself: it draws the track when each
// stage can have STAGE_MIN, and the same stages as a list when not.
//
// STAGE_MIN is the widest word in any stage today ("Questionnaire", 83.5 px at
// the label's 12.5 px, measured in Chrome) plus the stage's 8 px of padding and
// a little air.
export const STAGE_MIN = 96

export function trackFits(width: number, stages: number): boolean {
  return width >= stages * STAGE_MIN
}

function useTrackFits(stages: number) {
  const box = useRef<HTMLDivElement>(null)
  // Drawn as a track until the first measurement, which is made before the
  // first paint, so nobody sees the wrong one.
  const [fits, setFits] = useState(true)
  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    // The diagram's own side padding is not the track's.
    const measure = () => setFits(trackFits(el.clientWidth - 8, stages))
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const watch = new ResizeObserver(measure)
    watch.observe(el)
    return () => watch.disconnect()
  }, [stages])
  return [box, fits] as const
}


function editFromHash(): string {
  const q = window.location.hash.split('?')[1] ?? ''
  return new URLSearchParams(q).get('edit') ?? ''
}

export function Workflows() {
  const [list, setList] = useState<Workflow[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [editing, setEditing] = useState(editFromHash)

  const load = useCallback(() => {
    fetchWorkflows()
      .then((r) => {
        setList(r.workflows)
        setError(null)
      })
      .catch(setError)
  }, [])

  useEffect(load, [load])

  useEffect(() => {
    const onHash = () => setEditing(editFromHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  function closeEditor() {
    setEditing('')
    window.location.hash = '/settings/workflows'
  }

  if (error) return <ErrorBox error={error} what="The workflows" />
  if (!list) return <Loading what="the workflows" />

  return (
    <div className="settings-stack">
      <section className="panel wf-intro">
        <h2>Workflows</h2>
        <p className="obs-what">The processes this programme runs, as they are set up today</p>
        <nav className="wf-index" aria-label="Workflows on this page">
          {list.map((w) => (
            <a key={w.key} href={'#/settings/workflows'} onClick={(e) => jumpTo(e, w.key)}>
              {w.name}
            </a>
          ))}
        </nav>
      </section>

      {list.map((w) => (
        <WorkflowCard key={w.key} workflow={w} />
      ))}

      <RemediationDeadlines open={editing === EDIT_REMEDIATION} onClose={closeEditor} onSaved={load} />
      <VendorRequests open={editing === EDIT_REQUESTS} onClose={closeEditor} onSaved={load} />
    </div>
  )
}

// The index scrolls to a card rather than changing the address, which holds
// the pane and, for the deadlines, the editor.
function jumpTo(e: MouseEvent, key: string) {
  e.preventDefault()
  document.getElementById('wf-' + key)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function whoWords(a: WorkflowAuthority): string {
  return a.personas.length > 0 ? a.personas.join(', ') : 'Nobody in the shipped roles'
}

function WorkflowCard({ workflow: w }: { workflow: Workflow }) {
  const [variantKey, setVariantKey] = useState(w.variants[0]?.key ?? '')
  const variant = w.variants.find((v) => v.key === variantKey) ?? w.variants[0]
  // A catalogue shows the path, not a case on it, so every stage is drawn as
  // one still to come.
  const stages: Stage[] = (variant?.stages ?? []).map((s) => ({ key: s.key, label: s.label, sub: s.sub, state: 'upcoming' }))
  const [diagram, fits] = useTrackFits(stages.length)

  return (
    <section className="panel wf-card" id={'wf-' + w.key} aria-labelledby={'wf-h-' + w.key}>
      <div className="wf-head">
        <div>
          <h3 id={'wf-h-' + w.key}>{w.name}</h3>
          <p className="obs-what">{w.purpose}</p>
        </div>
        <div className="wf-edit">
          {w.editors.map((e) => (
            <a key={e.href} className="button" href={e.href}>
              Edit {e.label}
            </a>
          ))}
        </div>
      </div>

      {w.variants.length > 1 && (
        <div className="wf-seg" role="group" aria-label={'Show ' + w.name.toLowerCase() + ' for'}>
          {w.variants.map((v) => (
            <button key={v.key} type="button" aria-pressed={v.key === variant?.key} onClick={() => setVariantKey(v.key)}>
              {v.label}
            </button>
          ))}
        </div>
      )}

      <div ref={diagram} className={'wf-diagram' + (fits ? '' : ' wf-listed')}>
        <div className="wf-track">
          <StageTrack stages={stages} label={w.name + (variant?.label ? ', ' + variant.label : '') + ', ' + stages.length + ' stages'} />
        </div>
        {/* The same stages as a list, for a width where the track would
            squeeze them past reading. The diagram decides which one shows. */}
        <ol className="wf-steps">
          {stages.map((s, i) => (
            <li key={s.key} className="kit-upcoming">
              <span className="kit-node">{i + 1}</span>
              <span>
                <span className="wf-step-label">{s.label}</span>
                {s.sub && <span className="wf-step-sub">{s.sub}</span>}
              </span>
            </li>
          ))}
        </ol>
      </div>

      {w.settings && w.settings.length > 0 && (
        <dl className="wf-settings">
          {w.settings.map((s) => (
            <div key={s.label}>
              <dt>{s.label}</dt>
              <dd>{s.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="wf-facts">
        <div>
          <span className="field-label">You can change</span>
          <ul>
            {w.configurable.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
        {w.fixed && w.fixed.length > 0 && (
          <div>
            <span className="field-label">Always</span>
            <ul>
              {w.fixed.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        )}
        <div>
          <span className="field-label">Who may change it</span>
          <p>{whoWords(w.changedBy)}</p>
          {w.putInUseBy && (
            <p className="field-note">A second person puts a change in use: {whoWords(w.putInUseBy)}</p>
          )}
        </div>
      </div>

      {w.source && <p className="field-note wf-source">{w.source}</p>}
    </section>
  )
}
